/* Exor Games - price history on every single (card pages).

   What: sections/product-template.liquid renders, right under the price
   block and only when product.type contains "Single", an empty hidden
   mount:
     <div class="xg-ph" data-xg-ph data-product-id="123" hidden></div>
   This script asks the exor-binder worker once for that product's price
   history - GET /price-history.json?id=<id>, nightly snapshots kept in a
   Durable Object: the first variant's price (points), the cheapest
   variant's, and since 2026-09-10 the dearest variant's price as its own
   series (top) - and fills the mount with:
     - a 30-day step sparkline (inline SVG; stroke and area fill use
       currentColor, so the line takes the wrapper's ink token);
     - a badge for the last seven days: "Down 12% this week", "Up 8% this
       week" or "Steady this week" (plain-text arrows, no emoji), shown
       only once the worker has a full week to compare against;
     - the live price of the variant the shopper has picked, with the
       variant's name under it;
     - the tracking start date as a bare "09/02/26" in the header corner.

   The widget FOLLOWS THE PICKED VARIANT (owner, 2026-09-09: "a foil card
   priced at 500 and a non foil at 25 bucks will only show the one
   originally clicked on"; and a card whose only stock was the foil showed
   the plain card's number). It reads the store's own product JSON
   (/products/<handle>.js) once for the variant list with live prices,
   watches the theme's variant select (native change, jQuery-triggered
   change, the price node the theme rewrites, and a slow poll as the
   catch-all) and re-renders from the cached history:
     - a variant's KIND is its title without the condition word: "" for
       the plain card, "Foil", "Holofoil", "Reverse Holofoil", ...;
     - the first variant's kind uses the worker's base series (points); the
       kind whose Near Mint is the dearest variant uses the top series; any
       other kind has no honest history yet, so the mount hides rather than
       draw the wrong line;
     - conditions below Near Mint are priced by the store as a fixed
       multiplier of Near Mint, so the series is scaled by the live variant
       price over the live top price of the same kind; the big number is
       always the picked variant's live price.
   With fewer than two daily points there is nothing to draw, so only
   "Price tracking started Sep 2, 2026" shows. If the request fails (7s
   abort), the worker answers ok:false, or the points are empty, the mount
   stays hidden and the page looks exactly as it did before.

   Why: "is this a good price right now?" is the question every singles
   shopper has; a small honest trend line answers it on the page.

   Colours: modern-skin tokens with light fallbacks (--xg-surface,
   --xg-border, --xg-ink, --xg-text, --xg-muted, --xg-red, --xg-success,
   --xg-inverse-text), so dark mode follows xg-dark.css, which redefines
   the same tokens under html[data-xg-theme="dark"]. No hard-coded greys.
   The trend badges are FILLS (white on green / red), the pairing the dark
   theme keeps for red, because red-as-text is too faint on a dark ground.

   ES5 only, every step guarded: a failure here can never break a product
   page. No backslashes and no triple quotes on purpose (character-class
   regexes and String.fromCharCode instead), so the file also survives a
   GraphQL block-string upload. */
(function () {
  'use strict';

  var WORKER = 'https://exor-binder.nevski.workers.dev';
  var DAYS = 30;            /* days drawn */
  var TIMEOUT_MS = 7000;
  var NO_PRICE_CENTS = 99999900;   /* the theme's "email us for pricing" sentinel */
  var POLL_MS = 700;        /* catch-all watch on the variant select */
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var ARROW_DOWN = String.fromCharCode(8595);
  var ARROW_UP = String.fromCharCode(8593);
  var ARROW_FLAT = String.fromCharCode(8594);
  var DOT = ' ' + String.fromCharCode(183) + ' ';

  var CSS = '.xg-ph{margin:10px 0 14px;padding:10px 12px;border:1px solid var(--xg-border,#e3e6e8);border-radius:10px;background:var(--xg-surface,#ffffff);color:var(--xg-text,#3d454b);font-size:13px;line-height:1.35;max-width:440px}' +
    '.xg-ph[hidden]{display:none}' +
    '.xg-ph__head{display:flex;align-items:center;justify-content:space-between;gap:8px;margin:0 0 6px}' +
    '.xg-ph__label{font-weight:600;font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:var(--xg-ink,#171b1d)}' +
    '.xg-ph__badge{font-weight:600;font-size:12px;line-height:1.2;border-radius:999px;padding:3px 9px;border:1px solid var(--xg-border,#e3e6e8);color:var(--xg-muted,#707a83);white-space:nowrap}' +
    '.xg-ph__badge--down{background:var(--xg-success,#17784a);border-color:var(--xg-success,#17784a);color:var(--xg-inverse-text,#ffffff)}' +
    '.xg-ph__badge--up{background:var(--xg-red,#d62c28);border-color:var(--xg-red,#d62c28);color:var(--xg-inverse-text,#ffffff)}' +
    '.xg-ph__row{display:flex;align-items:center;gap:12px}' +
    '.xg-ph__chart{flex:1 1 auto;min-width:0;color:var(--xg-ink,#171b1d)}' +
    '.xg-ph__svg{display:block;width:100%;height:56px}' +
    '.xg-ph__now{flex:0 0 auto;text-align:right;max-width:46%}' +
    '.xg-ph__price{display:block;font-weight:700;font-size:16px;line-height:1.2;color:var(--xg-ink,#171b1d)}' +
    '.xg-ph__sub{display:block;font-size:11px;color:var(--xg-muted,#707a83)}' +
    '.xg-ph__note{margin:6px 0 0;font-size:11px;line-height:1.3;color:var(--xg-muted,#707a83)}' +
    '.xg-ph__from{font-size:10.5px;font-weight:600;letter-spacing:.03em;color:var(--xg-muted,#707a83);white-space:nowrap}';

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; });
  }

  function isNum(n) { return typeof n === 'number' && isFinite(n); }
  function trim(s) { return String(s == null ? '' : s).replace(/^[ ]+|[ ]+$/g, ''); }

  /* "2026-09-02" -> "Sep 2, 2026"; anything else -> "" */
  function prettyDate(iso) {
    var parts = String(iso || '').split('-');
    if (parts.length !== 3) return '';
    var y = parseInt(parts[0], 10), m = parseInt(parts[1], 10), d = parseInt(parts[2], 10);
    if (!(y > 1970) || !(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return '';
    return MONTHS[m - 1] + ' ' + d + ', ' + y;
  }

  /* "2026-09-02" -> "09/02/26". The owner wanted the tracking start as a
     bare date tucked in the corner, not a sentence under the chart. */
  function shortDate(iso) {
    var parts = String(iso || '').split('-');
    if (parts.length !== 3) return '';
    var y = parseInt(parts[0], 10), m = parseInt(parts[1], 10), d = parseInt(parts[2], 10);
    if (!(y > 1970) || !(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return '';
    return pad2(m) + '/' + pad2(d) + '/' + pad2(y % 100);
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  /* 1234.5 -> "$1,234.50" (the store prices in CAD, shown as the page does) */
  function money(n) {
    var fixed = Math.abs(n).toFixed(2);
    var whole = fixed.slice(0, -3), frac = fixed.slice(-3), out = '';
    while (whole.length > 3) { out = ',' + whole.slice(-3) + out; whole = whole.slice(0, -3); }
    return (n < 0 ? '-' : '') + '$' + whole + out + frac;
  }

  /* Clean [date, price] pairs out of whatever the worker sent */
  function cleanPoints(list) {
    var out = [];
    if (!list || typeof list.length !== 'number') return out;
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      if (!p || typeof p.length !== 'number' || p.length < 2) continue;
      var v = Number(p[1]);
      if (typeof p[0] !== 'string' || !isNum(v)) continue;
      out.push([p[0], v]);
    }
    return out;
  }

  /* Step sparkline: a day's price holds until the next day's, so the path
     walks right, then up or down. viewBox 0 0 240 56, stretched to the
     wrapper's width (non-scaling stroke keeps the line 2px). */
  function sparkline(points) {
    var W = 240, H = 56, PAD = 3, n = points.length, i;
    var min = Infinity, max = -Infinity;
    for (i = 0; i < n; i++) {
      if (points[i][1] < min) min = points[i][1];
      if (points[i][1] > max) max = points[i][1];
    }
    var span = max - min;
    function x(k) { return (PAD + (k * (W - 2 * PAD)) / (n - 1)).toFixed(1); }
    function y(v) { return (span > 0 ? PAD + ((max - v) * (H - 2 * PAD)) / span : H / 2).toFixed(1); }
    var line = 'M' + x(0) + ' ' + y(points[0][1]);
    for (i = 1; i < n; i++) line += 'H' + x(i) + 'V' + y(points[i][1]);
    var area = line + 'V' + (H - PAD) + 'H' + x(0) + 'Z';
    return '<svg class="xg-ph__svg" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none" focusable="false" aria-hidden="true">' +
      '<path class="xg-ph__area" d="' + area + '" fill="currentColor" fill-opacity="0.12" stroke="none"></path>' +
      '<path class="xg-ph__line" d="' + line + '" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"></path>' +
      '</svg>';
  }

  /* Last-seven-days badge from the worker's change7d (null = not tracked a
     week yet: no badge). Under half a percent either way reads as steady.
     A percentage survives the condition multiplier untouched. */
  function badge(change) {
    if (!change || !isNum(Number(change.pct))) return null;
    var pct = Number(change.pct);
    if (Math.abs(pct) < 0.5) return { cls: 'steady', text: ARROW_FLAT + ' Steady this week' };
    var n = Math.round(Math.abs(pct));
    if (pct < 0) return { cls: 'down', text: ARROW_DOWN + ' Down ' + n + '% this week' };
    return { cls: 'up', text: ARROW_UP + ' Up ' + n + '% this week' };
  }

  function ensureStyle() {
    if (document.getElementById('xg-ph-style')) return;
    var st = document.createElement('style');
    st.id = 'xg-ph-style';
    st.type = 'text/css';
    if (st.styleSheet) st.styleSheet.cssText = CSS; else st.appendChild(document.createTextNode(CSS));
    (document.head || document.getElementsByTagName('head')[0] || document.body).appendChild(st);
  }

  /* ---- variants --------------------------------------------------------- */

  /* "Near Mint Foil" -> "Foil"; "Near Mint" -> ""; "Damaged Reverse
     Holofoil" -> "Reverse Holofoil". The kind is what has its own base
     price; the condition in front is a store multiplier of it. */
  function kindOf(title) {
    return trim(String(title || '').replace(/^(near mint|lightly played|moderately played|heavily played|damaged)( |$)/i, ''));
  }

  function validCents(c) { return isNum(c) && c > 0 && c < NO_PRICE_CENTS; }

  /* The dearest live price among the variants of one kind (Near Mint on
     this store); 0 when none is priced. */
  function topCentsOfKind(variants, kind) {
    var top = 0;
    for (var i = 0; i < variants.length; i++) {
      if (kindOf(variants[i].title) !== kind) continue;
      if (validCents(variants[i].price) && variants[i].price > top) top = variants[i].price;
    }
    return top;
  }

  /* The kind whose Near Mint is the dearest variant of the product: the
     worker's top series is that variant's history. null when nothing is
     priced. */
  function dearestKind(variants) {
    var top = 0, kind = null;
    for (var i = 0; i < variants.length; i++) {
      if (validCents(variants[i].price) && variants[i].price > top) { top = variants[i].price; kind = kindOf(variants[i].title); }
    }
    return kind;
  }

  /* Series (points, current, change7d, since) for one kind, or null when
     the worker has no honest history for it. */
  function seriesFor(data, variants, kind) {
    var base = { points: data.points, current: data.current, change7d: data.change7d, since: data.since };
    if (!variants || !variants.length || kind == null) return base;
    var firstKind = kindOf(variants[0].title);
    if (kind === firstKind) return base;
    var top = data.top;
    if (top && kind === dearestKind(variants)) return { points: top.points, current: top.current, change7d: top.change7d, since: top.since };
    return null;
  }

  /* The theme's master select carries the picked variant id */
  function pickedId() {
    var sel = null;
    try { sel = document.querySelector('select[name="id"]'); } catch (e) { sel = null; }
    if (sel && sel.value) return String(sel.value);
    var m = String(window.location.search || '').match(/[?&]variant=([0-9]+)/);
    return m ? m[1] : '';
  }

  function variantById(variants, id) {
    for (var i = 0; i < variants.length; i++) if (String(variants[i].id) === String(id)) return variants[i];
    return null;
  }

  /* Build the widget inside the mount for the picked variant; true when
     something was shown, false (mount hidden) otherwise. */
  function render(el, data, variants, picked) {
    if (!data || data.ok !== true) return hide(el);
    var v = picked ? variantById(variants, picked) : null;
    var kind = v ? kindOf(v.title) : null;
    var series = seriesFor(data, variants, kind);
    if (!series) return hide(el);
    var pts = cleanPoints(series.points);
    if (!pts.length) return hide(el);
    var since = prettyDate(series.since) || prettyDate(pts[0][0]);
    var from = shortDate(series.since) || shortDate(pts[0][0]);
    var html;
    if (pts.length < 2) {
      html = '<p class="xg-ph__note">Price tracking started' + (since ? ' ' + esc(since) : '') + '</p>';
    } else {
      /* the picked condition's live price over the kind's Near Mint: the
         store prices conditions as fixed multipliers, so the history scales */
      var m = 1;
      var topCents = v ? topCentsOfKind(variants, kind) : 0;
      if (v && validCents(v.price) && topCents > 0) m = v.price / topCents;
      var last = pts.slice(-DAYS), scaled = [], i;
      for (i = 0; i < last.length; i++) scaled.push([last[i][0], last[i][1] * m]);
      var cur = (v && validCents(v.price)) ? v.price / 100 :
        (series.current && isNum(Number(series.current.price)) ? Number(series.current.price) * m : scaled[scaled.length - 1][1]);
      var b = badge(series.change7d);
      html = '<div class="xg-ph__head"><span class="xg-ph__label">Price history</span>' +
        (b ? '<span class="xg-ph__badge xg-ph__badge--' + b.cls + '">' + esc(b.text) + '</span>' : '') +
        (from ? '<span class="xg-ph__from">' + esc(from) + '</span>' : '') +
        '</div>' +
        '<div class="xg-ph__row"><div class="xg-ph__chart">' + sparkline(scaled) + '</div>' +
        '<div class="xg-ph__now"><span class="xg-ph__price">' + esc(money(cur)) + '</span><span class="xg-ph__sub">' + (v ? esc(v.title) + DOT : '') + 'today</span></div></div>';
    }
    ensureStyle();
    el.innerHTML = html;
    el.removeAttribute('hidden');
    try { el.hidden = false; } catch (e) {}
    return true;
  }

  function hide(el) {
    try { el.innerHTML = ''; el.setAttribute('hidden', ''); el.hidden = true; } catch (e) {}
    return false;
  }

  /* One request, 7s abort, cb(json | null); never throws, never calls twice. */
  function load(url, cb) {
    var done = false;
    function finish(v) { if (done) return; done = true; try { cb(v); } catch (e) {} }
    try {
      if (typeof fetch === 'function') {
        var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
        var tmo = setTimeout(function () { try { if (ctl) ctl.abort(); } catch (e) {} finish(null); }, TIMEOUT_MS);
        fetch(url, { signal: ctl ? ctl.signal : undefined, credentials: 'omit' })
          .then(function (r) { return r && r.ok ? r.json() : null; })
          .then(function (j) { clearTimeout(tmo); finish(j); }, function () { clearTimeout(tmo); finish(null); });
        return;
      }
      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.timeout = TIMEOUT_MS;
      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) return;
        var j = null;
        try { if (xhr.status === 200) j = JSON.parse(xhr.responseText); } catch (e) { j = null; }
        finish(j);
      };
      xhr.ontimeout = function () { finish(null); };
      xhr.onerror = function () { finish(null); };
      xhr.send(null);
    } catch (e) { finish(null); }
  }

  /* /products/<handle>.js on the store itself: variants with live prices in
     cents. Works under /collections/<c>/products/<h> too. */
  function productJsonUrl() {
    var path = String(window.location.pathname || '').replace(/[/]+$/, '');
    if (!/[/]products[/][^/]+$/.test(path)) return '';
    return path + '.js';
  }

  function cleanVariants(json) {
    var out = [];
    var list = json && json.variants;
    if (!list || typeof list.length !== 'number') return out;
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      if (!v || v.id == null) continue;
      out.push({ id: String(v.id), title: String(v.title || ''), price: Number(v.price) });
    }
    return out;
  }

  /* Re-render whenever the theme moves the variant select: native change
     (capture), jQuery-triggered change (no native event), the price node
     the theme rewrites, and a slow poll as the catch-all. */
  function watch(onChange) {
    try {
      document.addEventListener('change', function (e) {
        if (e.target && (e.target.name === 'id' || String(e.target.className).indexOf('single-option-selector') !== -1)) setTimeout(onChange, 80);
      }, true);
    } catch (e) {}
    try {
      if (window.jQuery) window.jQuery(document).on('change', '.single-option-selector, select[name="id"]', function () { setTimeout(onChange, 80); });
    } catch (e) {}
    try {
      var priceEl = document.querySelector('[id^="ProductPrice-"]');
      if (priceEl && window.MutationObserver) new MutationObserver(function () { onChange(); }).observe(priceEl, { childList: true, subtree: true, characterData: true });
    } catch (e) {}
    try { setInterval(onChange, POLL_MS); } catch (e) {}
  }

  function init() {
    var el = null;
    try { el = document.querySelector('[data-xg-ph]'); } catch (e) { el = null; }
    if (!el) return;
    var id = String(el.getAttribute('data-product-id') || '').replace(/[^0-9]/g, '');
    if (!id) return;
    var history = null, variants = [], pending = 2, shownFor = null;
    function paint() {
      var picked = pickedId();
      if (picked === shownFor) return;
      shownFor = picked;
      try { render(el, history, variants, picked); } catch (e) { hide(el); }
    }
    function ready() {
      if (--pending > 0) return;
      paint();
      watch(paint);
    }
    load(WORKER + '/price-history.json?id=' + encodeURIComponent(id), function (data) { history = data; ready(); });
    var pj = productJsonUrl();
    if (pj) load(pj, function (json) { variants = cleanVariants(json); ready(); });
    else ready();
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
  } catch (e) {}
})();
